import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Container, Row, Col, Accordion, Card, Button, Form } from 'react-bootstrap';
import { gather_source, ReportPlot } from './Javascript/GenerateReport.js';
import * as V from './Javascript/VesselReport.js';
import * as RO from './Javascript/ReportObjects.js';
import * as IN from './Javascript/IncidentsReport.js';
import * as G from './Javascript/General.js';
import * as TR from './Javascript/TimelineReport.js';
import * as RP from './Javascript/ReportPlots.js';
import RouteIncidents from './RouteIncidents';
import MapComponent from './MapComponent';
import AreaTable from './AreaTable';
import TimelineTable from './TimelineTable';
import './CSS/style.css';

interface Props {
    map_ref: any,
}

const TimelineReport: React.FC<Props> = ({map_ref}) => {
    const [is_loading, set_is_loading] = useState(true);
    const [removeNotIn, setRemoveNotIn] = useState(false);
    const [removePies, setRemovePies] = useState(false);
    const [areaTimelineReport, setAreaTimelineReport] = useState(true);
    const [areaTimelineSummary, setAreaTimelineSummary] = useState(true);
    const [positionGranularity, setPositionGranularity] = useState("d");
    const [dateFrom, setDateFrom] = useState<Date | null>(null);
    const [dateTo, setDateTo] = useState<Date | null>(null);
    const [vesselImo, setVesselImo] = useState<String | null>(null);
    const [vesselMmsi, setVesselMmsi] = useState<String | null>(null);
    const [vesselUuid, setVesselUuid] = useState<String | null>(null);
    const [vesselDetails, setVesselDetails] = useState<RO.Vessel | null>(null);
    const [travelDistance, setTravelDistance] = useState<number>(0)
    const [positionDetails, setPositionDetails] = useState<RO.PositionReport[] | null>(null);
    const [positionList, setPositionList] = useState<RO.Location[] | null>(null);
    const [timelinePiePlotData, setTimelinePiePlotData] = useState<RO.AllZonePlots[] | null>(null);
    const [timelineBarPlotData, setTimelineBarPlotData] = useState<RO.AllZonePlots[] | null>(null);
    const [spacialAreaList, setSpacialAreaList] = useState<RO.SpacialArea[] | null>(null);
    const [areaTimeline, setAreaTimeline] = useState<RO.AreaTimeline[] | null>(null);
    const [areaTotal, setAreaTotal] = useState<RO.AreaTotal[] | null>(null);
    const [positionTimeline, setPositionTimeline] = useState<RO.PositionAreaTimeline[] | null>(null);

    const area_types = [
        { key: "berthing_area_name", name: "Port"},
        { key: "eez", name: "EEZ"},
        { key: "jwc_zone", name: "JWC Zone"},
        { key: "sea_zone", name: "Sea Zone"},
        { key: "marpol_eca", name: "MARPOL ECA"},
        { key: "marpol_garbage", name: "MARPOL Garbage"},
        { key: "marpol_noxious_liquid", name: "MARPOL Noxious Liquid"},
        { key: "marpol_oil", name: "MARPOL Oil"},
        { key: "marpol_sewage", name: "MARPOL Sewage"},
    ]
    const default_types = [{ key: "berthing_area_name", name: "Port"},
        { key: "eez", name: "EEZ"},
        { key: "jwc_zone", name: "JWC Zone"},
        { key: "sea_zone", name: "Sea Zone"}];
    const [plotsDisplayed, setPlotsDisplayed] = useState<{ key: string, name: string }[]>(default_types);
    const [tablesDisplayed, setTablesDisplayed] = useState<{ key: string, name: string }[]>(default_types);

    const call_generate_pdf = async ( dl_type: string) => {
        let title_cont = document.querySelectorAll("#vessel_report_header");

        title_cont.forEach(function(cont) {
          let titlesToRemove = cont.querySelectorAll(".sub_title");
          titlesToRemove.forEach(function(title) { title.remove(); });
        });

        gather_source([], ["plots_container"], "page_break", map_ref);
        if(dl_type == 'pdf') gather_source([], ["plots_container"], "download_pdf", map_ref);
        else if(dl_type == 'word') gather_source([], ["plots_container"], "download_word", map_ref);
        else if(dl_type == 'excel') gather_source([], ["plots_container"], "download_excel", map_ref);
    };

    useEffect(() => {
        if(!is_loading){
            var download_btn = document.getElementById("download_pdf_btn") as HTMLButtonElement;
            if(download_btn){
                download_btn.disabled = false;
                download_btn.addEventListener('click', () => call_generate_pdf('pdf'));
            }
        }
    }, [is_loading]);

    useEffect(() => {
        const fetchParameters = async () => {
            const url_parameters = await TR.getUrlParameters();

            setVesselMmsi(url_parameters.mmsi);
            setVesselUuid(url_parameters.vesselUuid);
            setVesselImo(url_parameters.imo);
            if (url_parameters.date_from) setDateFrom(new Date(url_parameters.date_from));
            if (url_parameters.date_to) setDateTo(new Date(url_parameters.date_to));
        }
        if(is_loading) fetchParameters();
     }, []);

    useEffect(() => {
        const fetchVessel = async () => {
            const res = await V.getVesselsInfo(vesselUuid, vesselImo, vesselMmsi, null);
            setVesselDetails(res);
        };
        if(vesselImo != null || vesselMmsi != null || vesselUuid != null) fetchVessel();
    }, [vesselImo, vesselMmsi, vesselUuid]);

    useEffect(() => {
        const fetchPositions = async () => {
            if (vesselDetails?.vessel_identifier?.trim()) {
                const res = await TR.GetVesselHistoricPositions(vesselDetails.vessel_identifier, vesselDetails.imo, vesselDetails.mmsi, positionGranularity, dateFrom, dateTo);
                if(res != null) {
                    const distance = G.totalDistance(res);
                    if(distance) setTravelDistance(distance);
                    setPositionDetails(res);
                }
            }
        };
        if(vesselDetails != null) fetchPositions();
    }, [vesselDetails, positionGranularity, dateTo, dateFrom]);

    const reset_report = () => {
        setPositionDetails(null);
        setPositionList(null);
        setTimelinePiePlotData(null);
        setTimelineBarPlotData(null);
        setSpacialAreaList(null);
        setAreaTimeline(null);
        setAreaTotal(null);
        setPositionTimeline(null);
    }
    useEffect(() => {
        reset_report()
    }, [positionGranularity, dateFrom, dateTo]);

    useEffect(() => {
        const fetchSpacialArea = async () => {
            if(positionDetails != null) {
                setPositionList(positionDetails.map((item, index) => {
                     var add_position: RO.Location = {
                         id: item.row ? item.row.toString() : index.toString(),
                         dec: {
                             lat: item.latitude,
                             lon: item.longitude
                         },
                         Ddm: null,
                         Dms: null
                     };
                     return add_position;
                }));

                const { position_areas_list, areas_timeline_list, areas_total_list, combined } = await TR.checkSpacialAreasTimeline(positionDetails, dateFrom, dateTo);
                setAreaTotal(areas_total_list);
                setSpacialAreaList(position_areas_list);
                setAreaTimeline(areas_timeline_list);
                setPositionTimeline(combined);
            }
        }
        fetchSpacialArea();
    }, [positionDetails]);
    let renderedCount = 0;
    useEffect(() => {
        const fetchPlots = async () =>{
            if (!positionTimeline || positionTimeline.length === 0) return;
            const { pie_plots, bar_plots } = await TR.generateTimelineCharts(areaTotal, positionTimeline[0].position.date_received,
                positionTimeline[positionTimeline.length-1].position.date_received, removePies);
            setTimelinePiePlotData(pie_plots);
            setTimelineBarPlotData(bar_plots);

            var download_btn = document.getElementById("download_pdf_btn") as HTMLButtonElement;
            if(download_btn){
                download_btn.disabled = false;
                download_btn.addEventListener('click', () => call_generate_pdf('pdf'));
            }
        }
        if(areaTotal && positionTimeline && positionTimeline.length > 0) fetchPlots();
    },[areaTotal, removePies, removeNotIn]);

    const customise_displayed_spacial = async(event: React.ChangeEvent<HTMLInputElement>) => {
        const check_value = event.target.value.replace("_table", "").replace("_plot", "");
        const area_type = area_types.filter(i => i.key == check_value);

        if(event.target.checked){
            if(event.target.value.includes("_table")) {
                setTablesDisplayed((prev) => [...prev, area_type[0]]);
            }
            if(event.target.value.includes("_plot")) {
                setPlotsDisplayed((prev) => [...prev, area_type[0]]);
            }
        }
        else{
            if(event.target.value.includes("_table")) {
                const res = tablesDisplayed.filter(i => i.key != check_value);
                setTablesDisplayed(res);
            }
            if(event.target.value.includes("_plot")) {
                const res = plotsDisplayed.filter(i => i.key != check_value);
                setPlotsDisplayed(res);
            }
        }
    }
    const reloadMap = () => {
        window.location.reload();
    };

    return (
       <div id="timeline_container">
            {is_loading && vesselDetails == null ? (
                <div id="loading">Loading Vessel Details...<span className="rotating-arrows">&#8635;</span></div>
            ) : (
            <>
                <div id="top_btn_panel">
                    <button type="button" id="download_pdf_btn" disabled> Download PDF </button>
                    <button style={{display:'none'}} type="button" onClick={() => call_generate_pdf('word')}> Download Word </button>
                    <button style={{display:'none'}} type="button" onClick={() => call_generate_pdf('excel')}> Download excel </button>
                    <div className="top_menu_item">
                          <p>Select From Date:</p>
                          <div className="datepicker-wrapper">
                          <DatePicker
                            value={dateFrom ? dateFrom.toISOString().split('T')[0] : undefined}
                            onChange={(val: Date | null) => {if (val) setDateFrom(val);
                                else setDateFrom(null);
                              }}
                            className="form-control"
                            openToDate={dateFrom ? dateFrom : undefined}/>
                          </div>
                    </div>
                    <div className="top_menu_item">
                          <p>Select To Date:</p>
                          <DatePicker value={dateTo ? dateTo.toISOString().split('T')[0] : undefined}
                            onChange={(val: Date | null) => {
                                if (val) setDateFrom(val);
                                else setDateFrom(null);
                              }}
                            className="form-control" openToDate={dateTo ? dateTo : undefined} />
                    </div>
                    <div className="top_menu_item">
                        <p>Data Granularity</p>
                        <select value={positionGranularity} onChange={(event) => setPositionGranularity(event.target.value)}
                            className="form-control"> <option value="d">Daily</option> <option value="h">Hourly</option>
                        </select>
                    </div>
                    <div className="top_menu_item">
                        <input type="checkbox" onChange={(event) => setAreaTimelineReport(event.target.checked)} defaultChecked={areaTimelineReport}/>
                        <p>Display Area Timeline</p>
                    </div>
                    <div className="top_menu_item">
                        <input type="checkbox" onChange={(event) => setAreaTimelineSummary(event.target.checked)} defaultChecked={areaTimelineSummary}/>
                        <p>Display Area Summary</p>
                    </div>
                    <div className="top_menu_item">
                        <input type="checkbox" onChange={(event) => setRemoveNotIn(event.target.checked)}/>
                        <p>Remove Not In data</p>
                    </div>
                    <div className="top_menu_item">
                        <input type="checkbox" onChange={(event) => setRemovePies(event.target.checked)}/>
                        <p>Remove Pie Plots</p>
                    </div>
                    <div className="top_menu_item">
                        <table className="display_area_controls">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Plot</th>
                                    <th>Table</th>
                                </tr>
                            </thead>
                            <tbody>
                                {area_types ? area_types.map((item) =>
                                <tr key={item.key+"_display"}>
                                    <td>{item.name}</td>
                                    <td><input type="checkbox" onChange={(event) => customise_displayed_spacial(event)}
                                    value={item.key + "_plot"} checked={plotsDisplayed.some(i => i.key === item.key)}/></td>
                                    <td><input type="checkbox" onChange={(event) => customise_displayed_spacial(event)}
                                    value={item.key + "_table"} defaultChecked={tablesDisplayed.some(i => i.key === item.key)}/></td>
                                </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                    <div className="top_menu_item">
                        <button type="button" className="button_style" onClick={reloadMap}>Reload Report</button>
                    </div>
                </div>
                <div id="timeline_report_header" className="report_row">
                    { vesselDetails?.flag?.alpha_3_code ? (
                    <div className="left">
                        <img src={`Images/Assets/Flags/${vesselDetails?.flag.alpha_3_code.toUpperCase()}_flag.png`} />
                    </div>
                    ) : null}
                    <div className="right">
                        <h1 id="report_title">{G.displayValue(vesselDetails?.name)} Timeline: {G.displayValue(dateFrom?.toISOString().split("T")[0])} / {G.displayValue(dateTo?.toISOString().split("T")[0])}</h1>
                    </div>
                    <div className="clear_both"></div>
                </div>
               <div id="timeline_report_page1" className="report_row">
                   <div className="inner_padding">
                       <div className="left">
                           <p className="sub_title">Vessel Information</p>
                           <table className="singleIncident_table">
                           <tbody>
                               <tr>
                                   <td><span className="title">Name</span> <br /> { G.displayValue(vesselDetails?.name) }</td>
                               </tr>
                               <tr>
                                   <td><span className="title">Type</span> <br /> { G.displayValue(vesselDetails?.type) }</td>
                               </tr>
                               <tr>
                                   <td><span className="title">IMO</span> <br /> { G.displayValue(vesselDetails?.imo) }</td>
                               </tr>
                               <tr>
                                   <td><span className="title">MMSI</span> <br /> { G.displayValue(vesselDetails?.mmsi) }</td>
                               </tr>
                               <tr>
                                   <td><span className="title">Flag</span> <br /> { G.displayValue(vesselDetails?.flag?.name) }</td>
                               </tr>
                               <tr>
                                   <td><span className="title">DWT</span> <br /> { G.displayValue(vesselDetails?.dwt) }</td>
                               </tr>
                               <tr>
                                   <td><span className="title">Length (M)</span> <br /> { G.displayValue(vesselDetails?.length) }</td>
                               </tr>
                               <tr>
                                   <td><span className="title">Breadth (m)</span> <br /> { G.displayValue(vesselDetails?.breadth) }</td>
                               </tr>
                               {travelDistance !=0 && (<tr>
                                  <td><span className="title">Traveled Distance</span> <br /> {travelDistance}km / {(travelDistance * 0.539957).toFixed(0)}nm</td>
                               </tr>)}
                           </tbody>
                           </table>
                       </div>
                       <div className="right">
                            {positionList != null && map_ref != null && (
                                <MapComponent report_data={null} map_ref={map_ref} markers={null} path_points={positionList}/>
                            )}
                       </div>
                   </div>
               </div>
               <div className="report_row"><div className="page_break"></div></div>
               {is_loading && positionDetails == null ? (
                   <div className="loading_section_container">
                       <div className="loading_section">
                           <p>Loading Position Data...</p><span className="rotating-arrows">&#8635;</span>
                       </div>
                   </div>
               ) : (
               <>
                    {is_loading && areaTotal == null && spacialAreaList == null && areaTimeline == null ? (
                        <div className="loading_section_container">
                            <div className="loading_section">
                                <p>Loading Spacial Data...</p><span className="rotating-arrows">&#8635;</span>
                            </div>
                        </div>
                    ) : (
                    <>
                        {timelinePiePlotData && timelinePiePlotData.map((timelinePlotOrig, index) => {
                            const plotName = timelinePlotOrig?.plot_name?.replace(" Timeline", "").toUpperCase().trim();
                            const displayPlot = plotsDisplayed.some(item => item.name.toUpperCase().trim() === plotName);
                            if (!displayPlot ) return null;

                            let timelinePlot = timelinePlotOrig;
                            let timelineBarPlotDataTemp = timelineBarPlotData;

                            if(removeNotIn) {
                                timelinePlot = TR.removeNotInDataPie(timelinePlot);
                                if (timelineBarPlotDataTemp && timelineBarPlotDataTemp[index]) timelineBarPlotDataTemp[index] = TR.removeNotInDataBar(timelineBarPlotDataTemp[index]);
                            }
                            renderedCount += 1;
                            return (
                                <>

                                {(renderedCount-1) % 2 === 0 && (
                                    <div className="report_row">
                                        <div className="page_break"></div>
                                    </div>
                                )}
                                <div key={"pie_plot_" + index} className="report_row pie_bar_plot_container timeline_plots">
                                    <div className="inner_padding">
                                        {removePies ? (<div style={{width:"0px"}} className="left"></div>) : (
                                        <div className="left">
                                            <p className="sub_title">{timelinePlot?.plot_name}</p>
                                            <ReportPlot
                                                plotData={timelinePlot}
                                                title={timelinePlot?.plot_name}
                                                errorMessage="Timeline plot failed"
                                                plot_only={true}
                                            />
                                            <div className="clear_both"></div>
                                        </div>
                                        )}

                                        <div className="right" style={removePies ? { maxWidth: 1150 } : undefined}>
                                            <p className="sub_title"> </p>
                                            {timelineBarPlotDataTemp && timelineBarPlotDataTemp[index] ? (
                                                <ReportPlot
                                                    plotData={timelineBarPlotDataTemp[index]}
                                                    title={timelineBarPlotDataTemp[index]?.plot_name}
                                                    errorMessage="Timeline plot failed"
                                                    plot_only={true}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                                </>
                            );
                        })}

                        {tablesDisplayed && areaTimeline != null ? tablesDisplayed.map((item, index) =>
                            <AreaTable key={"area_table_"+index} areaTimeline={areaTimeline} type={item.key} table_title={item.name}
                            timeline_start={dateFrom} timeline_end={dateTo} areaTimelineReport={areaTimelineReport}
                            areaTimelineSummary={areaTimelineSummary} positionGranularity={positionGranularity}/>
                        ) : null}
                        {positionTimeline != null && (
                            <TimelineTable positionTimeline={positionTimeline} />
                        )}
                    </>
                    )}
                </>
                )}
            </>
            )}
       </div>
   );
}
export default TimelineReport;